import React from 'react';
import { Pressable, ScrollView, Text, View, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';
import { BottomSheet } from '../primitives/BottomSheet';
import { ENTRY_META } from '../entryMeta';
import { DAY_PROFILE, dayFullKey, FAN_REASONS } from '../constants';
import { TimeChip } from '../primitives/TimeChip';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import { RemoteImage } from '../../../shared/RemoteImage';
import { SideFoodChips } from '../SideFoodChips';
import type { SideFoodsProps } from '../sideFoodsState';
import { FanChipBar } from '../FanChipBar';
import type { FanChipBarProps } from '../useFanChoice';
import { resolveRecipeTitle } from '../../../../utils/recipeTitle';

export interface FanOptionM {
  key: string;
  recipe: RecipeIndexEntry;
  consequence?: string;
  reasonIdx: number;
  /** Label and stripe for an option that is not one of FAN_REASONS (an accompaniment). */
  reason?: { labelKey?: string; accent: string };
}

interface Props {
  visible: boolean;
  di: number;
  currentMealLabel: string;
  /** True when no existing dish is targeted (opened via "+") — hides Move/Remove and adapts copy. */
  isEmpty?: boolean;
  /** True when stacking an extra dish onto a slot that already holds one. */
  isAdding?: boolean;
  /** Only the slot's primary dish can be moved — swapping is slot-level. */
  canMove?: boolean;
  /**
   * The title of the dish the list is choosing an accompaniment for (#746).
   * The sheet then reads « Pour aller avec » over that title, and drops everything that acts on the
   * slot itself — move, remove, restaurant, leftovers: the main stays.
   */
  sidesFor?: string;
  /**
   * The foods the cook can ask the accompaniment list to use (#853), with the
   * ones picked. Only read while choosing an accompaniment.
   */
  sideFoods?: SideFoodsProps;
  /** « 5 nouvelles » and the meal's chips (#864). Takes the place of the divider. */
  chipBar?: FanChipBarProps;
  options: FanOptionM[];
  onSelect: (recipeId: string) => void;
  onMove: () => void;
  onRemove: () => void;
  onConvert?: (kind: 'dining' | 'leftover' | 'skip' | 'note') => void;
  onLibrary: () => void;
  onClose: () => void;
}

export function FanSheet({ visible, di, currentMealLabel, isEmpty, isAdding, canMove = true, sidesFor, sideFoods, chipBar, options, onSelect, onMove, onRemove, onConvert, onLibrary, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const profile = DAY_PROFILE[di];
  const sides = sidesFor != null;

  return (
    <BottomSheet visible={visible} onClose={onClose} maxHeight="92%">
      {sides && <View testID="sides-sheet" />}
      {/* Header */}
      <View style={styles.header}>
        {/* The day's profile (« Rapide ») is advice for choosing a dinner. */}
        {!sides && (
          <View style={[styles.dayBadge, { backgroundColor: profile.accent + '22' }]}>
            <Text style={[styles.dayBadgeText, { color: profile.accent }]}>{t(profile.tagKey)}</Text>
          </View>
        )}
        <View style={styles.headerInfo}>
          <Text style={styles.headerTitle}>
            {sides
              ? t('thisWeek.goesWith')
              : isAdding ? t('thisWeek.addDishTitle') : t('thisWeek.fanTitle', { count: options.length })}
          </Text>
          <Text style={styles.headerSub}>
            {sides ? sidesFor : `${t(dayFullKey(di))} · ${currentMealLabel}`}
          </Text>
        </View>
      </View>

      {sides && !!sideFoods && <SideFoodChips {...sideFoods} inset={16} />}

      {/* Secondary actions — only relevant when the slot already holds a meal */}
      {!isEmpty && !sides && (
        <View style={styles.secondaryRow}>
          {canMove && (
            <Pressable style={styles.secondaryBtn} onPress={onMove}>
              <Text style={styles.secondaryBtnText}>{t('thisWeek.move')}</Text>
            </Pressable>
          )}
          <Pressable style={styles.secondaryBtn} onPress={onRemove}>
            <Text style={[styles.secondaryBtnText, { color: Paprika.textMuted }]}>{t('thisWeek.remove')}</Text>
          </Pressable>
        </View>
      )}

      {/* Conversion row */}
      {onConvert && !sides && (
        <View style={styles.conversionRow}>
          <Text style={styles.conversionLabel}>{t('thisWeek.noRecipeShort')}</Text>
          <View style={styles.conversionButtons}>
            <Pressable
              style={[styles.conversionBtn, { backgroundColor: ENTRY_META.dining.tint }]}
              onPress={() => onConvert('dining')}
            >
              <Text style={styles.conversionBtnText}>{t('entry.restaurantShort')}</Text>
            </Pressable>
            <Pressable
              style={[styles.conversionBtn, { backgroundColor: ENTRY_META.leftover.tint }]}
              onPress={() => onConvert('leftover')}
            >
              <Text style={styles.conversionBtnText}>{t('entry.leftovers')}</Text>
            </Pressable>
            <Pressable
              style={[styles.conversionBtn, { backgroundColor: ENTRY_META.note.tint }]}
              onPress={() => onConvert('note')}
            >
              <Text style={styles.conversionBtnText}>{t('entry.note')}</Text>
            </Pressable>
            <Pressable
              style={[styles.conversionBtn, { backgroundColor: ENTRY_META.skip.tint }]}
              onPress={() => onConvert('skip')}
            >
              <Text style={styles.conversionBtnText}>{t('entry.nothingShort')}</Text>
            </Pressable>
          </View>
        </View>
      )}

      {!sides && !!chipBar && <FanChipBar {...chipBar} inset={16} />}

      {!sides && !chipBar && (
        <View style={styles.divider}>
          <View style={styles.dividerLine} />
          <Text style={styles.dividerText}>
            {isAdding ? t('thisWeek.orAddDish') : isEmpty ? t('thisWeek.orChooseRecipe') : t('thisWeek.orReplaceWith')}
          </Text>
          <View style={styles.dividerLine} />
        </View>
      )}

      {/* Empty state — library too small to suggest alternatives */}
      {options.length === 0 && (
        <Text style={styles.fanEmpty}>
          {sides ? t('thisWeek.sidesEmpty') : t('thisWeek.fanEmpty')}
        </Text>
      )}

      {/* Fan rows */}
      {options.map((opt) => {
        const reason = opt.reason ?? FAN_REASONS[opt.reasonIdx];
        return (
          <Pressable key={opt.key} style={styles.fanRow} onPress={() => onSelect(opt.recipe.id)} testID={`fan-option-${opt.recipe.id}`}>
            <View style={[styles.stripe, { backgroundColor: reason?.accent ?? Paprika.primary }]} />
            <View style={styles.fanPhoto}>
              <RemoteImage
                uri={opt.recipe.image}
                variant="thumb"
                style={styles.fanPhotoImg}
                resizeMode="cover"
                fallback={<View style={[styles.fanPhotoImg, styles.fanPhotoPlaceholder]} />}
              />
            </View>
            <View style={styles.fanInfo}>
              {!!reason?.labelKey && <Text style={styles.fanReason}>{t(reason.labelKey)}</Text>}
              <Text style={styles.fanTitle} numberOfLines={2}>{resolveRecipeTitle(opt.recipe, i18n.language)}</Text>
              <View style={styles.fanMeta}>
                {opt.recipe.readyInMinutes != null && <TimeChip minutes={opt.recipe.readyInMinutes} />}
              </View>
              {!!opt.consequence && (
                <Text style={styles.fanConsequence} numberOfLines={1}>{opt.consequence}</Text>
              )}
            </View>
          </Pressable>
        );
      })}

      {/* Library CTA */}
      <Pressable style={styles.libraryBtn} onPress={onLibrary} testID="fan-library">
        <Text style={styles.libraryBtnText}>{sides ? t('thisWeek.sidesOther') : t('thisWeek.browseLibrary')}</Text>
      </Pressable>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 12,
  },
  dayBadge: {
    borderRadius: 100,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  dayBadgeText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
  },
  headerInfo: {
    flex: 1,
    gap: 2,
  },
  headerTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 18,
    color: Paprika.textPrimary,
  },
  headerSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
  },
  secondaryRow: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  secondaryBtn: {
    borderWidth: 1.5,
    borderColor: Paprika.border,
    borderRadius: 100,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  secondaryBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: Paprika.border,
  },
  dividerText: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
    fontStyle: 'italic',
  },
  fanEmpty: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    lineHeight: 19,
    color: Paprika.textMuted,
    paddingHorizontal: 18,
    paddingVertical: 8,
  },
  fanRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0,
    marginHorizontal: 16,
    marginBottom: 8,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: Paprika.surfaceLowest,
    borderWidth: 1,
    borderColor: Paprika.border,
  },
  stripe: {
    width: 5,
    alignSelf: 'stretch',
  },
  fanPhoto: {
    width: 80,
    height: 80,
    backgroundColor: Paprika.bgSunk,
  },
  fanPhotoImg: {
    width: '100%' as any,
    height: '100%' as any,
  },
  fanPhotoPlaceholder: {
    backgroundColor: Paprika.bgSunk,
  },
  fanInfo: {
    flex: 1,
    padding: 10,
    gap: 3,
  },
  fanReason: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
  },
  fanTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    lineHeight: 18,
    color: Paprika.textPrimary,
  },
  fanMeta: {
    flexDirection: 'row',
    gap: 4,
  },
  fanConsequence: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
    fontStyle: 'italic',
  },
  libraryBtn: {
    marginHorizontal: 16,
    marginTop: 8,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    borderRadius: 100,
    paddingVertical: 13,
    alignItems: 'center',
  },
  libraryBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textSecondary,
  },
  conversionRow: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
  },
  conversionLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    fontWeight: '500' as const,
    color: Paprika.textSecondary,
  },
  conversionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  conversionBtn: {
    flex: 1,
    height: 34,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  conversionBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    fontWeight: '500' as const,
    color: Paprika.textPrimary,
  },
});
